import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import { PageHead } from "@/components/Filters";
import { api, errMsg } from "@/lib/api";

const TABS = [
  { key: "events", label: "Timeline events" },
  { key: "notices", label: "Notices" },
  { key: "videos", label: "YouTube videos" },
  { key: "webinars", label: "Webinars" },
  { key: "settings", label: "Community & hero" },
];

const EMPTY_FORMS = {
  events: { counselling_body: "KEA", name: "", event_date: "", status: "upcoming", description: "", order: 0 },
  notices: { title: "", body: "", link: "", category: "general", is_pinned: false },
  videos: { title: "", youtube_id: "", description: "" },
  webinars: {
    title: "",
    description: "",
    scheduled_at: "",
    register_url: "",
    recording_url: "",
    status: "upcoming",
  },
};

const Field = ({ label, children, testId }) => (
  <label className="flex flex-col gap-1" data-testid={testId}>
    <span className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">{label}</span>
    {children}
  </label>
);

const inputCls = "h-9 border border-slate-300 px-2 text-sm";
const areaCls = "border border-slate-300 p-2 text-sm";
const submitCls =
  "h-9 bg-[#1A365D] px-4 text-[11px] font-semibold uppercase tracking-widest text-white hover:bg-[#122845]";

function EventForm({ form, setForm, onSubmit, busy }) {
  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));
  return (
    <form onSubmit={onSubmit} className="grid gap-3 border border-slate-200 bg-white p-4 md:grid-cols-3">
      <Field label="Counselling body" testId="event-field-body">
        <input className={inputCls} value={form.counselling_body} onChange={set("counselling_body")} />
      </Field>
      <Field label="Name" testId="event-field-name">
        <input className={inputCls} value={form.name} onChange={set("name")} required />
      </Field>
      <Field label="Date" testId="event-field-date">
        <input className={inputCls} type="date" value={form.event_date} onChange={set("event_date")} />
      </Field>
      <Field label="Status" testId="event-field-status">
        <select className={inputCls} value={form.status} onChange={set("status")}>
          <option value="done">Done</option>
          <option value="current">Current</option>
          <option value="upcoming">Upcoming</option>
        </select>
      </Field>
      <Field label="Order" testId="event-field-order">
        <input className={inputCls} type="number" value={form.order} onChange={set("order")} />
      </Field>
      <Field label="Description" testId="event-field-desc">
        <input className={inputCls} value={form.description} onChange={set("description")} />
      </Field>
      <div className="md:col-span-3">
        <button data-testid="event-submit" type="submit" disabled={busy} className={submitCls}>
          {busy ? "Adding…" : "Add event"}
        </button>
      </div>
    </form>
  );
}

function NoticeForm({ form, setForm, onSubmit, busy }) {
  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));
  return (
    <form onSubmit={onSubmit} className="grid gap-3 border border-slate-200 bg-white p-4 md:grid-cols-2">
      <Field label="Title" testId="notice-field-title">
        <input className={inputCls} value={form.title} onChange={set("title")} required />
      </Field>
      <Field label="Category" testId="notice-field-category">
        <select className={inputCls} value={form.category} onChange={set("category")}>
          <option value="general">General</option>
          <option value="urgent">Urgent</option>
          <option value="download">Download</option>
          <option value="schedule">Schedule</option>
        </select>
      </Field>
      <Field label="Body" testId="notice-field-body">
        <textarea rows={3} className={areaCls} value={form.body} onChange={set("body")} />
      </Field>
      <Field label="Link (optional)" testId="notice-field-link">
        <input className={inputCls} value={form.link} onChange={set("link")} placeholder="https://…" />
      </Field>
      <label className="flex items-center gap-2 text-sm text-slate-600">
        <input
          data-testid="notice-field-pin"
          type="checkbox"
          checked={!!form.is_pinned}
          onChange={(e) => setForm((p) => ({ ...p, is_pinned: e.target.checked }))}
        />
        Pin to top
      </label>
      <div className="md:col-span-2">
        <button data-testid="notice-submit" type="submit" disabled={busy} className={submitCls}>
          {busy ? "Adding…" : "Add notice"}
        </button>
      </div>
    </form>
  );
}

function VideoForm({ form, setForm, onSubmit, busy }) {
  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));
  return (
    <form onSubmit={onSubmit} className="grid gap-3 border border-slate-200 bg-white p-4 md:grid-cols-2">
      <Field label="YouTube video id" testId="video-field-id">
        <input className={inputCls} value={form.youtube_id} onChange={set("youtube_id")} placeholder="dQw4w9WgXcQ" required />
      </Field>
      <Field label="Title" testId="video-field-title">
        <input className={inputCls} value={form.title} onChange={set("title")} required />
      </Field>
      <Field label="Description" testId="video-field-desc">
        <textarea rows={2} className={areaCls} value={form.description} onChange={set("description")} />
      </Field>
      <div className="md:col-span-2">
        <button data-testid="video-submit" type="submit" disabled={busy} className={submitCls}>
          {busy ? "Adding…" : "Add video"}
        </button>
      </div>
    </form>
  );
}

function WebinarForm({ form, setForm, onSubmit, busy }) {
  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));
  return (
    <form onSubmit={onSubmit} className="grid gap-3 border border-slate-200 bg-white p-4 md:grid-cols-2">
      <Field label="Title" testId="webinar-field-title">
        <input className={inputCls} value={form.title} onChange={set("title")} required />
      </Field>
      <Field label="Status" testId="webinar-field-status">
        <select className={inputCls} value={form.status} onChange={set("status")}>
          <option value="upcoming">Upcoming</option>
          <option value="past">Past</option>
        </select>
      </Field>
      <Field label="Scheduled at (date & time)" testId="webinar-field-time">
        <input
          className={inputCls}
          type="datetime-local"
          value={form.scheduled_at}
          onChange={set("scheduled_at")}
        />
      </Field>
      <Field label="Register URL" testId="webinar-field-register">
        <input className={inputCls} value={form.register_url} onChange={set("register_url")} placeholder="https://…" />
      </Field>
      <Field label="Recording URL" testId="webinar-field-recording">
        <input className={inputCls} value={form.recording_url} onChange={set("recording_url")} placeholder="https://…" />
      </Field>
      <Field label="Description" testId="webinar-field-desc">
        <textarea rows={2} className={areaCls} value={form.description} onChange={set("description")} />
      </Field>
      <div className="md:col-span-2">
        <button data-testid="webinar-submit" type="submit" disabled={busy} className={submitCls}>
          {busy ? "Adding…" : "Add webinar"}
        </button>
      </div>
    </form>
  );
}

function ItemList({ kind, items, onDelete }) {
  if (items.length === 0)
    return (
      <div data-testid={`${kind}-empty`} className="border border-dashed border-slate-300 p-6 text-center text-xs text-slate-400">
        Nothing here yet.
      </div>
    );
  return (
    <ul className="divide-y divide-slate-200 border border-slate-200 bg-white">
      {items.map((it) => (
        <li key={it.id} className="flex items-start justify-between gap-3 p-3 text-sm" data-testid={`${kind}-item-${it.id}`}>
          <div>
            <p className="font-semibold text-[#1A365D]">
              {it.title || it.name || it.youtube_id}
            </p>
            <p className="mt-0.5 text-xs text-slate-500">
              {it.status && <span className="uppercase tracking-widest">{it.status}</span>}
              {it.event_date && <span> · {it.event_date}</span>}
              {it.scheduled_at && <span> · {it.scheduled_at}</span>}
              {it.category && <span> · {it.category}</span>}
              {it.youtube_id && <span> · {it.youtube_id}</span>}
            </p>
            {it.description && <p className="mt-1 text-xs text-slate-600">{it.description}</p>}
            {it.body && <p className="mt-1 text-xs text-slate-600">{it.body}</p>}
            {it.link && (
              <a href={it.link} target="_blank" rel="noopener noreferrer" className="mt-1 block text-xs text-blue-700 hover:underline">
                {it.link}
              </a>
            )}
          </div>
          <button
            data-testid={`${kind}-delete-${it.id}`}
            onClick={() => onDelete(it.id)}
            className="mt-1 shrink-0 border border-slate-200 p-1.5 text-slate-500 hover:border-rose-400 hover:text-rose-600"
            aria-label="Delete"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </li>
      ))}
    </ul>
  );
}

function SettingsForm() {
  const [form, setForm] = useState({ telegram_url: "", whatsapp_url: "", hero_headline: "", hero_subline: "" });
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/admin/overview-settings")
      .then(({ data }) => setForm((prev) => ({ ...prev, ...data })))
      .catch((e) => toast.error(errMsg(e)))
      .finally(() => setLoading(false));
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/overview-settings", form);
      toast.success("Saved");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  if (loading) return <div className="text-sm text-slate-500">Loading…</div>;

  return (
    <form onSubmit={save} className="grid gap-3 border border-slate-200 bg-white p-4 md:grid-cols-2" data-testid="settings-form">
      <Field label="Telegram channel URL" testId="settings-field-telegram">
        <input className={inputCls} value={form.telegram_url || ""} onChange={set("telegram_url")} placeholder="https://t.me/…" />
      </Field>
      <Field label="WhatsApp channel URL" testId="settings-field-whatsapp">
        <input className={inputCls} value={form.whatsapp_url || ""} onChange={set("whatsapp_url")} placeholder="https://chat.whatsapp.com/…" />
      </Field>
      <Field label="Hero headline (optional)" testId="settings-field-headline">
        <input className={inputCls} value={form.hero_headline || ""} onChange={set("hero_headline")} />
      </Field>
      <Field label="Hero subline (optional)" testId="settings-field-subline">
        <input className={inputCls} value={form.hero_subline || ""} onChange={set("hero_subline")} />
      </Field>
      <div className="md:col-span-2">
        <button data-testid="settings-submit" type="submit" disabled={busy} className={submitCls}>
          {busy ? "Saving…" : "Save settings"}
        </button>
      </div>
    </form>
  );
}

export default function AdminOverview() {
  const [tab, setTab] = useState("events");
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY_FORMS.events);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (kind) => {
    if (kind === "settings") return;
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/overview/${kind}`);
      setItems(data.items);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    setForm(EMPTY_FORMS[tab] || {});
    if (tab !== "settings") load(tab);
  }, [tab, load]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form };
      if (tab === "events" && payload.order != null) payload.order = Number(payload.order);
      if (tab === "webinars" && payload.scheduled_at) {
        payload.scheduled_at = new Date(payload.scheduled_at).toISOString();
      }
      if (tab === "events" && payload.event_date) {
        payload.event_date = new Date(payload.event_date).toISOString();
      }
      await api.post(`/admin/overview/${tab}`, payload);
      toast.success("Added");
      setForm(EMPTY_FORMS[tab]);
      load(tab);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    try {
      await api.delete(`/admin/overview/${tab}/${id}`);
      load(tab);
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  return (
    <div className="space-y-5" data-testid="admin-overview-page">
      <PageHead
        title="Overview content"
        subtitle="Manage the counselling timeline, notices, videos, webinars, and community links shown on the user dashboard."
      />

      <div className="flex flex-wrap gap-1 border-b border-slate-200">
        {TABS.map((t) => (
          <button
            key={t.key}
            data-testid={`admin-overview-tab-${t.key}`}
            onClick={() => setTab(t.key)}
            className={`border-b-2 px-3 py-2 text-xs font-semibold uppercase tracking-widest transition-colors ${
              tab === t.key ? "border-[#1A365D] text-[#1A365D]" : "border-transparent text-slate-500 hover:text-slate-900"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "events" && <EventForm form={form} setForm={setForm} onSubmit={submit} busy={busy} />}
      {tab === "notices" && <NoticeForm form={form} setForm={setForm} onSubmit={submit} busy={busy} />}
      {tab === "videos" && <VideoForm form={form} setForm={setForm} onSubmit={submit} busy={busy} />}
      {tab === "webinars" && <WebinarForm form={form} setForm={setForm} onSubmit={submit} busy={busy} />}
      {tab === "settings" && <SettingsForm />}

      {tab !== "settings" && (
        <div className="space-y-2">
          <h3 className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">Existing items</h3>
          {loading ? (
            <div className="text-sm text-slate-500">Loading…</div>
          ) : (
            <ItemList kind={tab} items={items} onDelete={onDelete} />
          )}
        </div>
      )}
    </div>
  );
}
